import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cotAxios } from "./api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { FileSpreadsheet, Plus, Trash2 } from "lucide-react";
import useCotacaoUser from "./useCotacaoUser";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return iso; } };

export default function CotacaoListaPage() {
  const nav = useNavigate();
  const { canWrite, canDelete } = useCotacaoUser();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { carregar(); }, []);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await cotAxios.get("/cotacao/cotacoes");
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar");
    } finally { setLoading(false); }
  };

  const excluir = async (c) => {
    if (!window.confirm(`Excluir cotação nº ${c.numero}?`)) return;
    try {
      await cotAxios.delete(`/cotacao/cotacoes/${c.id}`);
      toast.success("Excluída");
      carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="cot-lista-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FileSpreadsheet className="w-6 h-6 text-blue-700" /> Cotações</h1>
          <p className="text-sm text-slate-500">Total: {rows.length}</p>
        </div>
        <Button onClick={() => nav("/cotacoes/dashboard/cotacoes/nova")} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-nova-cotacao" disabled={!canWrite} title={!canWrite ? "Somente admin e comprador podem criar cotações" : ""}>
          <Plus className="w-4 h-4 mr-1" /> Nova cotação
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-500">Carregando...</div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">
          Nenhuma cotação criada ainda. Clique em <b>Nova cotação</b> para começar.
        </CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {rows.map((c) => (
            <Card key={c.id} data-testid={`cotacao-${c.id}`} className="hover:border-blue-300 transition cursor-pointer" onClick={() => nav(`/cotacoes/dashboard/cotacoes/${c.id}`)}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold">Cotação nº {c.numero}</div>
                      {c.titulo && <span className="text-sm text-slate-600">— {c.titulo}</span>}
                      <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${c.status === "aberta" ? "bg-blue-100 text-blue-700" : c.status === "fechada" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        {(c.status || "").toUpperCase()}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1 flex gap-4 flex-wrap">
                      <span>{c._qtd_produtos} produto(s)</span>
                      <span>{c._qtd_fornecedores} fornecedor(es)</span>
                      <span>{c._qtd_respondidas} respondida(s)</span>
                      <span>Criada em {fmtDate(c.created_at)}</span>
                    </div>
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    {canDelete && (
                      <Button size="sm" variant="destructive" onClick={() => excluir(c)} data-testid={`btn-del-cot-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
